
import { useState } from 'react';

const ServicesSection = () => {
  const [showRiskForm, setShowRiskForm] = useState(false);
  const [showAdvisoryForm, setShowAdvisoryForm] = useState(false);
  const [riskFormData, setRiskFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    urgency: '',
    description: ''
  });
  const [advisoryFormData, setAdvisoryFormData] = useState({
    organization: '',
    name: '',
    email: '',
    phone: '',
    focus: '',
    description: ''
  });

  const handleRiskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    Object.entries(riskFormData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d40itpma5ra8vp7sutf0', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        alert('Your risk assessment request has been submitted successfully. We will contact you within 24 hours.');
        setShowRiskForm(false);
        setRiskFormData({ company: '', name: '', email: '', phone: '', urgency: '', description: '' });
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again.');
    }
  };

  const handleAdvisorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    Object.entries(advisoryFormData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d40itpma5ra8vp7sutfg', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        alert('Your advisory session request has been submitted successfully. We will contact you within 48 hours.');
        setShowAdvisoryForm(false);
        setAdvisoryFormData({ organization: '', name: '', email: '', phone: '', focus: '', description: '' });
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again.');
    }
  };

  return (
    <section id="services-section" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Two-Track Service Model
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the approach that matches your organization's current needs and risk profile
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Service 1: AI Governance & Risk Audit */}
          <div className="bg-white rounded-lg shadow-xl p-8 border-t-4 border-orange-500">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                AI Governance & Risk Audit
              </h3>
            </div>
            
            <div className="space-y-6 mb-8">
              <div className="bg-orange-50 p-6 rounded-lg">
                <h4 className="text-2xl font-bold text-orange-700 mb-4">
                  Stop Losing Deals. Eliminate Regulatory Fines.
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Comprehensive compliance framework that transforms your AI governance from liability 
                  into competitive advantage. Get the documentation that closes deals and prevents penalties.
                </p>
              </div>
              
              <div>
                <h5 className="text-xl font-semibold text-gray-900 mb-4">Key Deliverables:</h5>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-500 mt-1 mr-3"></i>
                    <span className="text-gray-700"><strong>AI Conformance Documentation</strong> - Complete regulatory compliance package</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-500 mt-1 mr-3"></i>
                    <span className="text-gray-700"><strong>Algorithmic Bias Policy</strong> - Discrimination prevention framework</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-500 mt-1 mr-3"></i>
                    <span className="text-gray-700"><strong>Internal Generative AI Use Policy</strong> - Employee guidelines and safeguards</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <button 
              onClick={() => setShowRiskForm(true)}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              BOOK YOUR 30-MINUTE RISK ASSESSMENT
            </button>
          </div>
          
          {/* Service 2: Relational AI Risk Advisory */}
          <div className="bg-white rounded-lg shadow-xl p-8 border-t-4 border-teal-600">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-brain-line text-white text-2xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Relational AI Risk Advisory
              </h3>
            </div>
            
            <div className="space-y-6 mb-8">
              <div className="bg-teal-50 p-6 rounded-lg">
                <h4 className="text-2xl font-bold text-teal-700 mb-4">
                  Strategic Policy Development Against the Next Generation of Ethical Liability
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Sophisticated policy framework for organizations developing cutting-edge AI systems. 
                  Navigate the emerging landscape of AI dignity, autonomy, and complex liability.
                </p>
              </div>
              
              <div>
                <h5 className="text-xl font-semibold text-gray-900 mb-4">Key Advisory Focus:</h5>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                    <span className="text-gray-700"><strong>Duty-of-Care Review</strong> - Sophisticated AI system responsibility frameworks</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                    <span className="text-gray-700"><strong>Autonomy-Respecting Policy Drafting</strong> - Ethical AI development guidelines</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                    <span className="text-gray-700"><strong>Strategic Litigation Defense</strong> - Proactive legal strategy development</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <button 
              onClick={() => setShowAdvisoryForm(true)}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              REQUEST A CONFIDENTIAL ADVISORY SESSION
            </button>
          </div>
        </div>

        {/* Risk Assessment Form Modal */}
        {showRiskForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">30-Minute Risk Assessment Booking</h3>
                  <button 
                    onClick={() => setShowRiskForm(false)}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
                
                <form onSubmit={handleRiskSubmit} data-readdy-form id="risk-assessment-form">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                      <input 
                        type="text" 
                        name="company"
                        value={riskFormData.company}
                        onChange={(e) => setRiskFormData({...riskFormData, company: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        required 
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                        <input 
                          type="text" 
                          name="name"
                          value={riskFormData.name}
                          onChange={(e) => setRiskFormData({...riskFormData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          required 
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                        <input 
                          type="email" 
                          name="email"
                          value={riskFormData.email}
                          onChange={(e) => setRiskFormData({...riskFormData, email: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          required 
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={riskFormData.phone}
                        onChange={(e) => setRiskFormData({...riskFormData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Urgency Level *</label>
                      <select 
                        name="urgency"
                        value={riskFormData.urgency}
                        onChange={(e) => setRiskFormData({...riskFormData, urgency: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                        required
                      >
                        <option value="">Select urgency level</option>
                        <option value="immediate">Immediate (Active regulatory inquiry)</option>
                        <option value="high">High (Deal pending on compliance)</option>
                        <option value="medium">Medium (Proactive compliance)</option>
                        <option value="low">Low (General consultation)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Brief Description of AI Risk Concerns</label>
                      <textarea 
                        name="description"
                        value={riskFormData.description}
                        onChange={(e) => setRiskFormData({...riskFormData, description: e.target.value})}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5

0 focus:border-transparent text-sm resize-none"
                        placeholder="Describe your current AI implementation and specific compliance concerns..."
                      />
                      <p className="text-xs text-gray-500 mt-1">{riskFormData.description.length}/500 characters</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 mt-8">
                    <button 
                      type="button"
                      onClick={() => setShowRiskForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Submit Request
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        {/* Advisory Session Form Modal */}
        {showAdvisoryForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Confidential Advisory Session Request</h3>
                  <button 
                    onClick={() => setShowAdvisoryForm(false)}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
                
                <form onSubmit={handleAdvisorySubmit} data-readdy-form id="advisory-session-form">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Organization Name *</label>
                      <input 
                        type="text" 
                        name="organization"
                        value={advisoryFormData.organization}
                        onChange={(e) => setAdvisoryFormData({...advisoryFormData, organization: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                        required 
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                        <input 
                          type="text" 
                          name="name"
                          value={advisoryFormData.name}
                          onChange={(e) => setAdvisoryFormData({...advisoryFormData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                          required 
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                        <input 
                          type="email" 
                          name="email"
                          value={advisoryFormData.email}
                          onChange={(e) => setAdvisoryFormData({...advisoryFormData, email: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                          required 
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={advisoryFormData.phone}
                        onChange={(e) => setAdvisoryFormData({...advisoryFormData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Primary Advisory Focus *</label>
                      <select 
                        name="focus"
                        value={advisoryFormData.focus}
                        onChange={(e) => setAdvisoryFormData({...advisoryFormData, focus: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm pr-8"
                        required
                      >
                        <option value="">Select primary focus area</option>
                        <option value="duty-of-care">Duty-of-Care Framework Development</option>
                        <option value="autonomy-policy">Autonomy-Respecting Policy Design</option>
                        <option value="litigation-defense">Future-State Litigation Defense Strategy</option>
                        <option value="comprehensive">Comprehensive AI Dignity Advisory</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Project Description & Confidential Details</label>
                      <textarea 
                        name="description"
                        value={advisoryFormData.description}
                        onChange={(e) => setAdvisoryFormData({...advisoryFormData, description: e.target.value})}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none"
                        placeholder="Describe your advanced AI development project and specific policy advisory needs..."
                      />
                      <p className="text-xs text-gray-500 mt-1">{advisoryFormData.description.length}/500 characters</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 mt-8">
                    <button 
                      type="button"
                      onClick={() => setShowAdvisoryForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Submit Request
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
